import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  Slider,
  ChargerCard,
  Spacer,
  SpacerSizeTypes,
  Modal,
  LayoutContainer,
  RfidCardStatusTypes,
  RfidCardUseTypes,
  useStore,
} from '@envision/ev-ui-component-library'
import { PageTheme } from '../../typeScript/types/PageTheme'
import { IRootState } from 'store'
import { ILoggedUser } from 'typeScript/interfaces/IUser'
import ExternalCardProvider from 'constants/externalCardProvider'
import UpdateRfidForm from './UpdateRfidForm'
import AddExternalRfidForm from './AddExternalRfidForm'
import api from 'api/api'
import { InnerSpinner, Page } from 'components'
import {
  rfidBusinessImage,
  rfidPrivateImage,
  ExternalRfidCardImage,
  modalRfidBusinessCardFocusImage,
  modalRfidBusinessSuccessImage,
  modalRfidPrivateCardFocusImage,
  modalRfidPrivateSuccessImage,
  modalExternalRfidCardFocusImage,
} from 'constants/images'
import loaderActions from '../../store/loader'
import { logger } from '@sentry/react'
import { useIsMounted } from '../../utils/hooks'

const UNIVERS_ISSUER = 'UNIVERS'

export interface IRfid {
  id: string
  visibleId: string
  type: string
  status: RfidCardStatusTypes
  use: RfidCardUseTypes
  isExternal: boolean
  attributes: Array<{ name: string; value: string }>
}

export interface CardIntegration {
  id: number
  cpo: string
  status: string
}

export interface Card {
  id: number
  contractId: string
  visualNumber: string
  issuer: string
  issuerId: string
  issuerCountryCode: string
  issuerTokenId: string
  ownerId: string
  issuerStatus: string
  status: string
  type: string
  useType: string
  whiteListType: string
  issuerLastUpdatedAt: string
  integrations: CardIntegration[]
}

const mapIntegrationStatus = (status: string): RfidCardStatusTypes => {
  switch (status) {
    case 'CREATED':
    case 'TO_CREATE':
    case 'NOT_CREATED':
    case 'TO_ASSIGN':
    case 'CREATING':
    case 'ASSIGNING':
      return RfidCardStatusTypes.INITIAL
    case 'ASSIGNED':
      return RfidCardStatusTypes.ORDERED
    case 'ACTIVATED':
      return RfidCardStatusTypes.ACTIVE
    case 'INACTIVATED':
      return RfidCardStatusTypes.INACTIVE
    case 'ASSIGNING_FAILED':
    case 'ACTIVATION_FAILED':
    case 'DEACTIVATION_FAILED':
    case 'CREATING_FAILED':
      return RfidCardStatusTypes.ERROR
    case 'TO_ACTIVE':
    case 'ACTIVATING':
    case 'TO_INACTIVE':
    case 'DEACTIVATING':
      return RfidCardStatusTypes.PROCESSING
    default:
      return RfidCardStatusTypes.INITIAL
  }
}

const ADD_EXTERNAL_RFID_MODAL_ID = 'add-external-rfid'

function ChargingCardsPage() {
  const { t } = useTranslation('rfid')
  const dispatch = useDispatch()
  const user = useSelector<IRootState, ILoggedUser | null>(
    (state) => state.user.user
  )
  const [rfidAssets, setRfidAssets] = useState<IRfid[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [eventEmitter] = useStore.eventEmitter()
  const [currentSlide, setCurrentSlide] = useState<number>(0)
  const [slideToIndex, setSlideToIndex] = useState<number>()
  const [hasTariff, setHasTariff] = useState<boolean>(false)
  const isMounted = useIsMounted()

  const canAddExternalCard: boolean =
    user?.externalCardUse === ExternalCardProvider.SHELL
  const canShowAddExternalCardButton: boolean =
    canAddExternalCard && user?.isReimbursementActivated === true

  useEffect(() => {
    fetchRfidAssets()
    getTariff()
  }, [])

  const getTariff = () => {
    api.payout
      .getPayoutHistory()
      .then((resp) => {
        const tariffs = resp.data.energyTariffs

        if (tariffs && tariffs.length > 0) {
          // Sorting by startDate
          tariffs.sort((a, b) => a.startDate + b.startDate)

          setHasTariff(true)
        }
      })
      .catch(() => {
        setHasTariff(false)
      })
  }

  const mapUseType = (useType: string): RfidCardUseTypes => {
    switch (useType) {
      case 'BUSINESS':
        return RfidCardUseTypes.BUSINESS
      case 'PRIVATE':
        return RfidCardUseTypes.PRIVATE
      default:
        return RfidCardUseTypes.PRIVATE
    }
  }

  const fetchRfidAssets = async () => {
    try {
      const response = await api.assets.getRFIDAssets()
      if (response?.data && Array.isArray(response.data)) {
        const cards: Card[] = response.data
        const rfidCards: IRfid[] = cards
          .filter((card) => card.integrations.some((i) => i.cpo === 'DRIIVZ'))
          .map((card) => {
            const driivzIntegration = card.integrations.find(
              (i) => i.cpo === 'DRIIVZ'
            )
            return {
              id: card.id.toString(),
              visibleId: card.visualNumber,
              type: card.type,
              status: driivzIntegration
                ? mapIntegrationStatus(driivzIntegration.status)
                : RfidCardStatusTypes.INITIAL,
              use: mapUseType(card.useType),
              isExternal: card.issuer !== UNIVERS_ISSUER,
              attributes: [],
            }
          })
        setRfidAssets(rfidCards)
      } else {
        setRfidAssets([])
      }
    } catch (error: any) {
      logger.error('Failed to fetch RFID assets', error)
      setRfidAssets([])
    } finally {
      setIsLoading(false)
      dispatch(loaderActions.setShowLoader(false))
    }
  }

  const passCurrentSlide = (index: number): void => {
    setSlideToIndex(index)
    setCurrentSlide(index)
  }

  const handleOpenAddExternalModal = (): void => {
    eventEmitter.emit('openModal', { id: ADD_EXTERNAL_RFID_MODAL_ID })
  }

  const handleExternalCardAdded = (): void => {
    fetchRfidAssets()
  }

  const updateCardStatusOptimistically = (
    cardId: string,
    newStatus: RfidCardStatusTypes
  ): void => {
    setRfidAssets((prevAssets) =>
      prevAssets.map((asset) =>
        asset.id === cardId ? { ...asset, status: newStatus } : asset
      )
    )
  }

  const showAppropriateRFIDStatus = (status: string) => {
    return status === RfidCardStatusTypes.INITIAL
      ? t('external_rfid_status_ordered')
      : status === RfidCardStatusTypes.ORDERED
        ? t('external_rfid_status_ordered')
        : status === RfidCardStatusTypes.ACTIVE
          ? t('external_rfid_status_active')
          : status === RfidCardStatusTypes.INACTIVE
            ? t('external_rfid_status_inactive')
            : status === RfidCardStatusTypes.PROCESSING
              ? t('external_rfid_status_pending')
              : status === RfidCardStatusTypes.ERROR
                ? t('external_rfid_status_error')
                : t('external_rfid_status_ordered')
  }

  const assetUseType = (asset: IRfid): RfidCardUseTypes => {
    if (!asset.isExternal && asset.use === RfidCardUseTypes.PRIVATE) {
      return RfidCardUseTypes.PRIVATE
    } else if (!asset.isExternal && asset.use === RfidCardUseTypes.BUSINESS) {
      return RfidCardUseTypes.BUSINESS
    }
    return RfidCardUseTypes.EXTERNAL
  }
  const renderSlider = () => (
    <>
      <Slider slideToIndex={slideToIndex} passCurrentSlide={passCurrentSlide}>
        {rfidAssets.map((asset, idx: number) => (
          <div key={idx} className="flex flex-col justify-center items-center">
            <ChargerCard
              className="w-fit"
              id={asset.id}
              customRFIDStatusComponent={
                <div className="border-[1px]  border-solid rounded-[20px] py-1 px-3">
                  {showAppropriateRFIDStatus(asset.status)}
                </div>
              }
              visibleId={asset.visibleId}
              status={asset.status}
              use={assetUseType(asset)}
              isCurrent={currentSlide === idx ? true : false}
              PrivateImagePath={rfidPrivateImage}
              BusinessImagePath={rfidBusinessImage}
              ExternalImagePath={ExternalRfidCardImage}
              textObj={{
                statusInitial: t('external_rfid_status_initial'),
                statusOrdered: t('external_rfid_status_ordered'),
                statusActive: t('external_rfid_status_active'),
                statusInactive: t('external_rfid_status_inactive'),
                statusError: t('external_rfid_status_error'),
                usePrivate: t('external_rfid_use_private'),
                useBusiness: t('external_rfid_use_business'),
                statusProcessing: t('external_rfid_status_processing'),
                useExternal: t('external_rfid_use_external'),
                cardActivated: t('external_rfid_button_activate_card'),
              }}
            />
            {asset.isExternal && currentSlide === idx && (
              <p
                style={{
                  maxWidth: 'fit-content',
                  width: '52ch',
                  textAlign: 'center',
                  padding: '10px 1rem',
                  lineHeight: '1.2',
                }}
                dangerouslySetInnerHTML={{
                  __html: t('external_rfid_card_status_info_message'),
                }}
                className="px-auto! bg-bg_ligth-grey-gradient rounded-[20px]"
              ></p>
            )}
          </div>
        ))}
      </Slider>
    </>
  )

  return (
    <Page
      pageTheme={PageTheme.light}
      title={t('external_rfid_title')}
      className="w-full"
      headerButton={
        canShowAddExternalCardButton
          ? {
              title: t('external_rfid_add_external_disabled_tooltip'),
              disabled: !hasTariff,
              label: t('external_rfid_add_external_button'),
              onClick: handleOpenAddExternalModal,
            }
          : undefined
      }
    >
      <InnerSpinner isActive={isLoading}>
        {isLoading ? null : rfidAssets.length === 0 ? (
          <p>{t('external_rfid_missing_text')}</p>
        ) : rfidAssets.length === 1 ? (
          <div className="relative lg:left-1/3">{renderSlider()}</div>
        ) : (
          <div style={{ paddingTop: '3rem' }}> {renderSlider()} </div>
        )}

        {rfidAssets.map((asset, idx: number) => (
          <div key={idx}>
            <Modal
              id={asset.id}
              titleRichtext={
                asset.status === RfidCardStatusTypes.ACTIVE
                  ? asset.visibleId
                  : t('external_rfid_modal_title')
              }
              isImageOverlay={false}
              renderedImage={
                asset.use === RfidCardUseTypes.PRIVATE ? (
                  asset.status === RfidCardStatusTypes.ORDERED ? (
                    <img src={modalRfidPrivateCardFocusImage} />
                  ) : (
                    <img src={modalRfidPrivateSuccessImage} />
                  )
                ) : asset.status === RfidCardStatusTypes.ORDERED ? (
                  <img src={modalRfidBusinessCardFocusImage} />
                ) : (
                  <img src={modalRfidBusinessSuccessImage} />
                )
              }
            >
              <UpdateRfidForm
                id={asset.id}
                status={asset.status}
                type={asset.type}
                onOptimisticUpdate={updateCardStatusOptimistically}
              />
            </Modal>
            <Spacer size={SpacerSizeTypes.XL} />
          </div>
        ))}
        <Spacer size={SpacerSizeTypes.LG} />
        <Modal
          id={ADD_EXTERNAL_RFID_MODAL_ID}
          titleRichtext={t('external_rfid_add_external_modal_title')}
          isImageOverlay={false}
          renderedImage={
            <img
              src={modalExternalRfidCardFocusImage}
              alt="External RFID Card"
            />
          }
        >
          <AddExternalRfidForm onSuccess={handleExternalCardAdded} />
        </Modal>
      </InnerSpinner>
    </Page>
  )
}

export default ChargingCardsPage
